import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthUtils } from './auth.utils';
import { AuthAppService } from '@modules/auth';
import { Observable, catchError, throwError, switchMap } from 'rxjs';

/**
 * Endpoints excluded from automatic 401 handling and token refresh
 * These paths should handle authentication errors manually
 */
const AUTH_EXCLUDED_ENDPOINTS = [
    '/api/auth/login',
    '/api/auth/logout',
    '/api/auth/register',
    '/api/auth/refresh-token'
] as const;

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthAppService);
    const router = inject(Router);

    const redirectToSignIn = (): void => {
        const redirectURL = location.pathname + location.search;
        router.navigate(['/sign-in'], { queryParams: { redirectURL } });
    };

    // Helper function to add auth header
    const addAuthHeader = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
        return request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`)
        });
    };

    // Helper function to proceed with request
    const proceedWithRequest = (request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> => {
        return next(request)
            .pipe(
                catchError((error) => {
                    // Catch "401 Unauthorized" responses
                    const isExcludedEndpoint = AUTH_EXCLUDED_ENDPOINTS.some(excludedPath =>
                        req.url.toLowerCase().includes(excludedPath.toLowerCase())
                    );

                    if (!isExcludedEndpoint && error instanceof HttpErrorResponse && error.status === 401) {
                        // Attempt token refresh first (handles cookie-based token rotation).
                        // The browser will automatically send the refresh_token cookie.
                        return authService.refreshCurrentToken().pipe(
                            switchMap((success) => {
                                if (success) {
                                    // Retry the original request — new access_token cookie is now set
                                    return next(request);
                                }
                                authService.signOut();
                                redirectToSignIn();
                                return throwError(() => error);
                            }),
                            catchError(() => {
                                authService.signOut();
                                redirectToSignIn();
                                return throwError(() => error);
                            })
                        );
                    }

                    return throwError(() => error);
                })
            );
    };

    const isExcludedEndpoint = AUTH_EXCLUDED_ENDPOINTS.some(excludedPath =>
        req.url.toLowerCase().includes(excludedPath.toLowerCase())
    );

    if (!req.url.includes('/api/') || isExcludedEndpoint) {
        return proceedWithRequest(req);
    }
    // Check token status and proceed accordingly
    if (!!authService.aToken && !AuthUtils.isTokenExpired(authService.aToken)) {
        // Token is valid, add it and proceed
        const newReq = addAuthHeader(req, authService.aToken);
        return proceedWithRequest(newReq);
    } else if (!!authService.aToken && !!authService.rToken) {
        // Token is expired but refresh token exists, refresh first then proceed
        return authService.refreshCurrentToken()
            .pipe(
                switchMap((success) => {
                    if (success && authService.aToken) {
                        // Refresh successful, proceed with new token
                        const newReq = addAuthHeader(req, authService.aToken);
                        return proceedWithRequest(newReq);
                    } else {
                        // Refresh failed, logout and redirect
                        authService.signOut();
                        redirectToSignIn();
                        return throwError(() => new Error('Token refresh failed'));
                    }
                }),
                catchError((err) => {
                    authService.signOut();
                    redirectToSignIn();
                    return throwError(() => err);
                })
            );
    } else {
        // No valid tokens, proceed without auth header (for public endpoints)
        return proceedWithRequest(req);
    }
};


